import { useCallback, useRef, useState } from 'react';
import { GridGroup } from '../types';

type MouseOrKeyboardEvent = React.MouseEvent | React.KeyboardEvent;

export const useGridSelection = (
  allItemIds: string[],
  initialSelected: string[],
  groups: GridGroup[],
  onChange?: (ids: string[]) => void,
) => {
  const [selectedPages, setSelectedPages] = useState<string[]>(initialSelected);
  const lastSelectedRef = useRef<string | null>(null);

  // Mirror the values handleSelectPage reads into refs so it doesn't need them
  // as useCallback deps — otherwise its identity changed on every selection and
  // reorder, and cascaded fresh props down through SortableThumbnailPage to
  // every ThumbnailPage in the grid.
  const selectedPagesRef = useRef(selectedPages);
  selectedPagesRef.current = selectedPages;

  const allItemIdsRef = useRef(allItemIds);
  allItemIdsRef.current = allItemIds;

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Map selected ids to their index in allItemIds
  const indexes = selectedPages
    .map((id) => allItemIds.indexOf(id))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);

  const isContiguous =
    indexes.length !== 0 ? indexes.every((v, i) => (i === 0 ? true : v === indexes[i - 1] + 1)) : false;

  const startOfRangeSelected = indexes[0] + 1;
  const endOfRangeSelected = indexes[indexes.length - 1] + 1;

  const handleSelectPage = useCallback((id: string, event: MouseOrKeyboardEvent) => {
    const current = selectedPagesRef.current;
    const currentItemIds = allItemIdsRef.current;
    let next: string[] = [];
    if (event.shiftKey && lastSelectedRef.current) {
      // Select range
      const lastIdx = currentItemIds.indexOf(lastSelectedRef.current);
      const currIdx = currentItemIds.indexOf(id);
      if (lastIdx !== -1 && currIdx !== -1) {
        const [start, end] = [lastIdx, currIdx].sort((a, b) => a - b);
        next = Array.from(new Set([...current, ...currentItemIds.slice(start, end + 1)]));
      } else {
        next = [id];
      }
    } else if (event.metaKey || event.ctrlKey) {
      // Toggle selection
      if (current.includes(id)) {
        next = current.filter((s) => s !== id);
      } else {
        next = [...current, id];
      }
      lastSelectedRef.current = id;
    } else {
      // Single select
      next = [id];
      lastSelectedRef.current = id;
    }
    setSelectedPages(next);
    onChangeRef.current?.(next);
  }, []);

  const handleSelectAll = useCallback(
    (groupId: string) => {
      const group = groups.find((g) => g.id === groupId);
      if (group && group.items.length > 0) {
        const newSelectedIds = group.items.map((item) => item.id);
        setSelectedPages(newSelectedIds);
        if (onChange) onChange(newSelectedIds);
      }
    },
    [groups, onChange],
  );

  const selectRange = useCallback(
    (startIndex: number, endIndex: number) => {
      const [start, end] = [startIndex, endIndex].sort((a, b) => a - b);
      const ids = allItemIds.slice(start, end + 1);
      setSelectedPages(ids);
      if (onChange) onChange(ids);
      lastSelectedRef.current = ids.length ? ids[ids.length - 1] : null;
    },
    [allItemIds, onChange],
  );

  return {
    selectedPages,
    isContiguous,
    startOfRangeSelected,
    endOfRangeSelected,
    handleSelectPage,
    handleSelectAll,
    selectRange,
  };
};
